<template lang="pug">
  #dashboard
    .card-header.bg-white
      div.form-inline
        div.mr-3.d-flex.align-items-center.justify-content-center
          span.mr-1 計測：
          toggle-button(:value="isTracking", :labels="true" @change="toggleTracking()")
        .form-group.mr-2
          select.form-control(@change="handleDateSelection", v-model="selectedDateOption")
            option(v-for="date in dateOptions" :key="date.k" :value="date.k") {{ date.v }}
            option(value="custom") カスタム
        .form-group.mr-2(v-if="selectedDateOption === 'custom'")
          vuejs-datepicker(@input="handleDateChange" v-model="startDate", :format="DatePickerFormat", :language="language", input-class="form-control")
        div.mr-2(v-if="selectedDateOption === 'custom'") ~
        .form-group.mr-2(v-if="selectedDateOption === 'custom'")
          vuejs-datepicker(@input="handleDateChange" v-model="endDate", :format="DatePickerFormat", :language="language", input-class="form-control")
        .form-group.mr-2
          .device-buttons
            button.mr-2(:class="['btn', selectedDeviceType === 1 ? 'btn-primary' : 'border']", @click="setDeviceType(1)")
              i.fas.fa-mobile-alt
              | SP
            button.mr-2(:class="['btn', selectedDeviceType === 0 ? 'btn-primary' : 'border']", @click="setDeviceType(0)")
              i.fas.fa-desktop
              | PC
          select.form-control.mr-2(
            v-model="selectedCvTagId"
            :disabled="isLoading"
          )
            option(value="") 全てのコンバージョン
            option(v-for="tag in cvTags" :key="tag.id" :value="tag.id") {{ tag.name }}
    .card-header.bg-white
      table.mb-4.w-100
        thead(style="border-bottom: 1px solid black;")
          tr
            th.py-2.text-center.col-2 PV数
            th.py-2.text-center.col-2 セッション数
            th.py-2.text-center.col-2 新規セッション数
            th.py-2.text-center.col-2 直帰数
            th.py-2.text-center.col-2 滞在時間（平均値）
            th.py-2.text-center.col-2 CV数
        tbody(style="border-bottom: 1px solid black;")
          tr
            td.py-2.text-center.col-2 {{ pv_count }}
            td.py-2.text-center.col-2 {{ session_count }}
            td.py-2.text-center.col-2
              | {{ new_session_count }}
              br
              div.text-center(v-if="session_count > 0") （{{ ((new_session_count / session_count) * 100).toFixed(1) }}％）
            td.py-2.text-center.col-2
              | {{ bounce_count }}
              br
              div.text-center(v-if="session_count > 0") （{{ ((bounce_count / session_count) * 100).toFixed(1) }}％）
            td.py-2.text-center.col-2 {{ average_stay_time }}秒
            td.py-2.text-center.col-2
              | {{ cv_count }}
              br
              div.text-center(v-if="session_count > 0") （{{ ((cv_count / pv_count) * 100).toFixed(1) }}％）
      div.chart-wrapper.d-flex.gap-4
        div.chart-filter(style="min-width: 200px; width: 200px")
          .select-button
            select.py-1.mb-4.w-100(
              style="border-radius: 6px; text-align: center;"
              v-model="selectedOption"
              :disabled="isLoading"
            )
              option(v-for="option in options" :key="option.k" :value="option.k") {{ option.v }}
          .period-buttons.d-flex.justify-content-between.w-100
            button(
              v-for="period in ['hourly', 'daily', 'weekly', 'monthly']"
              :key="period"
              :class="['period-button', 'flex-grow-1', 'mx-1', 'py-1', { active: selectedPeriod === period }]"
              @click="selectedPeriod = period"
              :disabled="isLoading"
            ) {{ periodLabel(period) }}
        div.chart-container.w-100(style="position: relative; height: 520px;")
          .loading-overlay(v-if="isLoading")
            .spinner-border.text-primary(role="status")
            span.ml-2 データを読み込み中...
          LineChart(:chart-data="chartData" :chart-options="chartOptions" ref="lineChart")
</template>

<script>
import { Line } from 'vue-chartjs'
import vuejsDatepicker from 'vuejs-datepicker';
import { ja } from 'vuejs-datepicker/dist/locale';
import { DatePickerFormat, dateOptions, formatDate, getDateRange } from '../../utils/dateUtils'

export default {
  name: 'Dashboard',
  components: {
    LineChart: {
      name: 'LineChart',
      extends: Line,
      props: ['chartData', 'chartOptions'],
      mounted() {
        this.renderChart(this.chartData, this.chartOptions);
        this.$nextTick(() => {
          if (this.$data._chart) {
            this.$data._chart.options.hover.onHover = (event, elements) => {
              if (elements && elements.length) {
                event.target.style.cursor = 'pointer';
              } else {
                event.target.style.cursor = 'default';
              }
            };
            const canvas = this.$data._chart.canvas;
            canvas.style.height = '520px';
          }
        });
      },
      methods: {
        updateChart() {
          if (this.$data._chart) {
            this.$data._chart.destroy();
            this.renderChart(this.chartData, this.chartOptions);
            this.$nextTick(() => {
              if (this.$data._chart) {
                const canvas = this.$data._chart.canvas;
                canvas.style.height = '520px';
              }
            });
          }
        }
      }
    },
    vuejsDatepicker
  },
  props: {
    initialData: {
      type: Object,
      required: true,
      validator: (value) => {
        return (
          value.is_tracking !== undefined &&
          value.target !== undefined &&
          value.date_option !== undefined &&
          value.custom_date_options !== undefined &&
          value.device !== undefined &&
          value.period !== undefined &&
          value.pv_count !== undefined &&
          value.session_count !== undefined &&
          value.new_session_count !== undefined &&
          value.bounce_count !== undefined &&
          value.average_stay_time !== undefined &&
          value.cv_count !== undefined &&
          value.datasets !== undefined &&
          value.labels !== undefined
        )
      }
    }
  },
  data() {
    return {
      isTracking: this.initialData.is_tracking ?? false,
      DatePickerFormat,
      language: ja,
      startDate: new Date(new Date().setDate(new Date().getDate() - 6)),
      endDate: new Date(new Date().setDate(new Date().getDate())),
      selectedDateOption: 'this_month',
      selectedDeviceType: this.initialData.device ?? 0,
      cvTags: [],
      selectedCvTagId: '',
      dateOptions,
      isLoading: false,
      selectedOption: this.initialData.target || 'pv',
      selectedPeriod: this.initialData.period || 'daily',
      options: [
        { k: 'pv', v: 'PV数' },
        { k: 'session', v: 'セッション数' },
        { k: 'new_session', v: '新規セッション数' },
        { k: 'bounce', v: '直帰数' },
        { k: 'stay_time', v: '滞在時間（平均値）' },
        { k: 'cv', v: 'CV数' }
      ],
      chartData: {
        labels: [],
        datasets: [
          {
            label: '',
            data: [],
            borderColor: 'rgb(220, 53, 69)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            fill: false,
            lineTension: 0.1
          }
        ]
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0
        },
        legend: {
          display: false
        },
        title: {
          display: false
        },
        tooltips: {
          enabled: true,
          mode: 'index',
          intersect: false,
          position: 'nearest',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleFontSize: 14,
          bodyFontSize: 14,
          displayColors: false,
          callbacks: {
            label: function(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label || '';
              const value = tooltipItem.yLabel;
              return `${label}: ${value}`;
            }
          }
        },
        hover: {
          mode: 'index',
          intersect: false,
          animationDuration: 0,
          onHover: function(event, elements) {
            if (elements && elements.length) {
              event.target.style.cursor = 'pointer';
            } else {
              event.target.style.cursor = 'default';
            }
          }
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true
            }
          }]
        }
      },
      target: null,
      period: null,
      pv_count: null,
      session_count: null,
      new_session_count: null,
      bounce_count: null,
      average_stay_time: null,
      cv_count: null,
      // デバウンス用のタイマー
      debounceTimer: null,
      // 最後のリクエストの情報
      lastRequestInfo: {
        params: null,
        timestamp: null,
        promise: null
      },
      // リクエストタイムアウト設定（ミリ秒）
      requestTimeout: 30000, // 30秒
      // 重複リクエストの最小間隔（ミリ秒）
      minRequestInterval: 2000, // 2秒
    }
  },
  created() {
    this.initializeData();
    this.fetchDashboardData();
  },
  methods: {
    async toggleTracking() {
      this.isTracking = !this.isTracking;
      const response = await fetch(`/users/tag_domains/switch_tracking_status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_tracking: this.isTracking
        })
      });
      const data = await response.json();
      this.isTracking = data.is_tracking;
    },
    periodLabel(period) {
      switch (period) {
        case 'hourly': return '時別';
        case 'daily': return '日別';
        case 'weekly': return '週別';
        case 'monthly': return '月別';
      }
    },
    initializeData() {
      this.target = this.initialData.target;
      this.period = this.initialData.period;
      this.device = this.initialData.device;
      this.pv_count = this.initialData.pv_count;
      this.session_count = this.initialData.session_count;
      this.new_session_count = this.initialData.new_session_count;
      this.bounce_count = this.initialData.bounce_count;
      this.average_stay_time = this.initialData.average_stay_time;
      this.cv_count = this.initialData.cv_count;
      this.selectedDeviceType = this.initialData.device ?? 0;
      this.selectedOption = this.initialData.target || 'pv';
      this.selectedPeriod = this.initialData.period || 'daily';

      // 初期グラフデータの設定
      const selectedLabel = this.options.find(opt => opt.k === this.selectedOption).v;
      const selectedDataset = this.initialData.datasets.find(ds => ds.label === selectedLabel);

      if (selectedDataset) {
        this.chartData = {
          labels: this.initialData.labels,
          datasets: [
            {
              label: selectedLabel,
              data: selectedDataset.data,
              borderColor: 'rgb(220, 53, 69)',
              backgroundColor: 'rgba(220, 53, 69, 0.2)',
              fill: false,
              lineTension: 0.1
            }
          ]
        };
      }
      this.$nextTick(() => {
        this.fetchDashboardData();
      });
    },
    async fetchDashboardData() {
      // デバウンス処理（300ms待機）
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      this.debounceTimer = setTimeout(async () => {
        await this._fetchDashboardData();
      }, 300);
    },
    
    async _fetchDashboardData() {
      this.isLoading = true;
      
      try {
        const params = new URLSearchParams({
          target: this.selectedOption,
          period: this.selectedPeriod,
          device: this.selectedDeviceType
        });

        if (this.selectedDateOption === 'custom') {
          params.append('custom_date_options[start_date]', formatDate(this.startDate));
          params.append('custom_date_options[end_date]', formatDate(this.endDate));
        } else {
          params.append('date_option', this.selectedDateOption);
        }

        if (this.selectedCvTagId) {
          params.append('cv_tag_id', this.selectedCvTagId);
        }

        const paramsString = params.toString();
        const currentTime = Date.now();
        
        // 重複リクエストの防止（改善版）
        if (this.lastRequestInfo.params === paramsString) {
          // 同じパラメータの場合、最小間隔をチェック
          if (this.lastRequestInfo.timestamp && 
              (currentTime - this.lastRequestInfo.timestamp) < this.minRequestInterval) {
            this.isLoading = false;
            return;
          }
          
          // 進行中のリクエストがある場合は、そのリクエストの完了を待つ
          if (this.lastRequestInfo.promise) {
            try {
              await this.lastRequestInfo.promise;
              this.isLoading = false;
              return;
            } catch (error) {
              // エラーが発生した場合は新しいリクエストを実行
            }
          }
        }
        
        // 新しいリクエストの情報を記録
        this.lastRequestInfo.params = paramsString;
        this.lastRequestInfo.timestamp = currentTime;
        
        // タイムアウト付きのリクエストを作成
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);
        
        const requestPromise = fetch(`/users/analytics/dashboard.json?${paramsString}`, {
          signal: controller.signal
        });
        
        this.lastRequestInfo.promise = requestPromise;
        
        const response = await requestPromise;
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();

        this.cvTags = data.cv_tags || [];

        this.pv_count = data.pv_count;
        this.session_count = data.session_count;
        this.new_session_count = data.new_session_count;
        this.bounce_count = data.bounce_count;
        this.average_stay_time = data.average_stay_time;
        this.cv_count = data.cv_count;

        const selectedLabel = this.options.find(opt => opt.k === this.selectedOption).v;
        const selectedDataset = data.datasets.find(ds => ds.label === selectedLabel);

        if (selectedDataset) {
          const newChartData = {
            labels: data.labels,
            datasets: [
              {
                label: selectedLabel,
                data: selectedDataset.data,
                borderColor: 'rgb(220, 53, 69)',
                backgroundColor: 'rgba(220, 53, 69, 0.2)',
                fill: false,
                lineTension: 0.1,
                pointRadius: 4,
                pointHoverRadius: 6
              }
            ]
          };
          this.chartData = newChartData;
          this.$nextTick(() => {
            if (this.$refs.lineChart) {
              this.$refs.lineChart.updateChart();
            }
          });
        } else {
          console.error('Dataset not found for label:', selectedLabel);
        }
        
        // リクエスト完了
        this.lastRequestInfo.promise = null;
        
      } catch (error) {
        if (error.name === 'AbortError') {
          console.error('Request timeout after', this.requestTimeout, 'ms');
        } else {
          console.error('Error fetching dashboard data:', error);
        }
        
        // エラー時はリセット
        this.lastRequestInfo.params = null;
        this.lastRequestInfo.promise = null;
      } finally {
        this.isLoading = false;
      }
    },
    handleDateSelection() {
      if (this.selectedDateOption === 'custom') {
        return;
      }
      const { start, end } = getDateRange(this.selectedDateOption);
      this.startDate = start;
      this.endDate = end;
      this.fetchDashboardData();
    },
    handleDateChange() {
      if (this.selectedDateOption === 'custom') {
        this.fetchDashboardData();
      }
    },
    setDeviceType(type) {
      if (this.selectedDeviceType !== type) {
        this.selectedDeviceType = type;
        this.fetchDashboardData();
      }
    },
  },
  watch: {
    selectedOption: {
      handler() {
        this.fetchDashboardData();
      }
    },
    selectedPeriod: {
      handler() {
        this.fetchDashboardData();
      }
    },
    selectedCvTagId: {
      handler() {
        this.fetchDashboardData();
      }
    }
  }
}
</script>
<style scoped>
.period-button {
  border-radius: 6px;
  border: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 6px 12px;
  background-color: white;
  color: #333;
  cursor: pointer;
  outline: none;
  transition: background-color 0.2s, color 0.2s;
}

.period-button.active {
  background-color: rgb(0, 123, 255);
  color: white;
}

.gap-4 {
  gap: 1.5rem;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}
</style>
